import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { RowActions } from "@/components/app/patterns/row-actions";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { readState } from "@/lib/query/read-state";
import { EscalationTargetSelect } from "./-components/escalation-target-select";
import { OnCallWindow } from "./-components/on-call-window";
import { OneBuildingCard } from "@/components/app/workspace/one-building-card";
import { PagedRules } from "../-components/paged-rules";

export const Route = createFileRoute("/_authenticated/_front-desk/sarah/escalation/")({
  component: Escalation,
});

interface Rule {
  staffId?: string | null;
  phone?: string | null;
  company?: string | null;
  /** What the rule is called on the call log and in the roster's own reading. */
  label?: string | null;
  /** What the first version of this editor wrote. Read, never written. */
  contactId?: string | null;
  trigger?: string | null;
  /* The agent has honoured these since the rules were written, and until now
     nothing could set them: every rule was on, every day, round the clock. */
  enabled?: boolean | null;
  days?: number[] | null;
  startTime?: string | null;
  endTime?: string | null;
}

/** The stored names win; the older ones are only a fallback for old rows. */
const targetOf = (rule: Rule) => rule.staffId ?? rule.contactId ?? "";
const triggerOf = (rule: Rule) => rule.company ?? rule.trigger ?? "";
/** A rule with nothing recorded is on, the same way the domain reads it. */
const liveOf = (rule: Rule) => rule.enabled !== false;
/** "Nobody yet" is no staffId at all: the save refuses an empty one. */
const forSaving = (rule: Rule): Rule => ({ ...rule, staffId: rule.staffId || null });

const fail = (error: Error) => toast.error(errorWords(error));

/** Callers the agent puts through ahead of the usual routing. */
function Escalation() {
  const { facilityId } = useFacility();
  const queryClient = useQueryClient();
  const realFacility = facilityId === "all" ? null : facilityId;
  /** Only a started edit lives in state. Until somebody changes something the table reads the
   * stored list, so a save elsewhere is not hidden behind a stale copy. */
  const [draft, setDraft] = useState<Rule[] | null>(null);

  const stored = useQuery({
    ...orpc.frontDesk.agent.escalation.queryOptions({
      input: { facilityId: realFacility ?? "" },
    }),
    enabled: Boolean(realFacility),
  });
  const targets = useQuery({
    ...orpc.frontDesk.agent.escalationTargets.queryOptions({
      input: { facilityId: realFacility ?? "" },
    }),
    enabled: Boolean(realFacility),
  });

  const rules = draft ?? (stored.data?.rules as Rule[] | undefined) ?? [];
  const failed = readState(stored) === "failed";
  const setRules = (next: Rule[] | ((previous: Rule[]) => Rule[])) =>
    setDraft(typeof next === "function" ? next(rules) : next);

  const save = useMutation(
    orpc.frontDesk.agent.saveEscalation.mutationOptions({
      onSuccess: async (result) => {
        toast.success("Saved");
        // What was saved is now the stored list; drop the draft so the table
        // reflects what the server actually kept, including rules it dropped.
        setDraft(null);
        void result;
        await queryClient.invalidateQueries({ queryKey: orpc.frontDesk.agent.key() });
      },
      onError: fail,
    }),
  );

  if (!realFacility) {
    return (
      <>
        <PageHeader title="Priority calls" meta="Callers put through ahead of the usual routing." />
        <OneBuildingCard prompt="Choose a building. Each one routes its own calls." />
      </>
    );
  }

  const addRule = () =>
    setRules((previous) => [...previous, { phone: "", company: "", staffId: null }]);
  const update = (index: number, patch: Partial<Rule>) =>
    setRules((previous) =>
      previous.map((rule, position) => (position === index ? { ...rule, ...patch } : rule)),
    );

  return (
    <>
      <PageHeader
        title="Priority calls"
        meta="Callers put through to a named person ahead of the usual routing."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" disabled={failed} onClick={addRule}>
              Add a rule
            </Button>
            <Button
              disabled={save.isPending || failed}
              onClick={() => save.mutate({ facilityId: realFacility, rules: rules.map(forSaving) })}
            >
              Save
            </Button>
          </div>
        }
      />

      {stored.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : failed ? (
        <ReadFailed
          message="Could not load the priority call rules."
          error={stored.error}
          retrying={stored.isFetching}
          onRetry={() => void stored.refetch()}
        />
      ) : rules.length > 0 ? (
        <PagedRules rules={rules}>
          {(page) => (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>On</TableHead>
                  <TableHead>What triggers it</TableHead>
                  <TableHead>Reach</TableHead>
                  <TableHead>Who it goes to</TableHead>
                  <TableHead>When it is awake</TableHead>
                  <TableHead>What to call it</TableHead>
                  <TableHead className="text-right">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {page.map(({ rule, index }) => (
                  <TableRow
                    /* Position, not content: a key built from the number remounted the
                   row on every keystroke, and the field lost focus after one digit.
                   Every cell is controlled, so a removed row cannot leave state behind. */
                    // oxlint-disable-next-line no-array-index-key
                    key={index}
                  >
                    <TableCell>
                      <Switch
                        checked={liveOf(rule)}
                        aria-label="Rule is in force"
                        onCheckedChange={(enabled) => update(index, { enabled })}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={triggerOf(rule)}
                        placeholder="Says it is urgent"
                        aria-label="What triggers it"
                        onChange={(event) =>
                          update(index, { company: event.target.value, trigger: null })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={rule.phone ?? ""}
                        placeholder="+1 512 555 0100"
                        aria-label="Reach"
                        onChange={(event) => update(index, { phone: event.target.value })}
                      />
                    </TableCell>
                    <TableCell>
                      <EscalationTargetSelect
                        targets={targets.data ?? []}
                        value={targetOf(rule)}
                        onChange={(staffId) =>
                          update(index, { staffId: staffId || null, contactId: null })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <OnCallWindow
                        days={rule.days ?? []}
                        startTime={rule.startTime ?? ""}
                        endTime={rule.endTime ?? ""}
                        onChange={(next) => update(index, next)}
                      />
                    </TableCell>
                    <TableCell>
                      {/* `label`, not `note`: the save accepts a note and stores
                      nothing, so anything typed into one was lost silently. */}
                      <Input
                        value={rule.label ?? ""}
                        placeholder="Daughter of a hospice resident"
                        aria-label="What to call it"
                        onChange={(event) => update(index, { label: event.target.value })}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <RowActions
                        label={rule.label || "this rule"}
                        items={[
                          {
                            label: "Remove",
                            destructive: true,
                            onSelect: () =>
                              setRules((previous) =>
                                previous.filter((_, position) => position !== index),
                              ),
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </PagedRules>
      ) : (
        <EmptyState
          title="No early routing"
          description="Every call follows the front desk staff list until a rule says otherwise."
        />
      )}

      <p className="text-muted-foreground max-w-prose text-sm">
        A rule needs both halves: something to recognise the caller by, and somebody on this
        building's front desk staff to reach. One with neither is dropped when you save, and one
        naming somebody without a telephone number is refused, because it could never fire.
      </p>
    </>
  );
}
